import React, { useState, useEffect } from "react";
import api from '../services/api';
import { STORAGE_URL } from "../services/api";
import logo from "../assets/logo.jpeg";
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from "lucide-react";

const SEOContent = () => (
  <section className="py-12 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 transition-colors duration-300">
    <div className="max-w-[1200px] mx-auto px-4 space-y-8">
      <div>
        <h3 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-5 relative inline-block">
          ABOUT US
          <span className="absolute left-0 -bottom-2 w-16 h-1 bg-[#FED547] rounded-full"></span>
        </h3>
        <p className="text-[17px] leading-8 text-gray-600 dark:text-gray-300 text-justify mt-6">
          <span className="font-bold text-gray-900 dark:text-white">KADTECH HR SOLUTIONS</span>{" "}
          is a professional HR consulting and talent acquisition company committed to helping organizations build high-performing teams through innovative, reliable, and cost-effective recruitment solutions.
          <br /><br />
          We specialize in connecting the right talent with the right opportunities across multiple industries, enabling businesses to grow while helping professionals build successful careers.
          <br /><br />
          With a customer-centric approach, technology-driven recruitment process, and experienced HR professionals, we deliver fast, efficient, and quality hiring solutions tailored to every business need.
        </p>
      </div>
    </div>
  </section>
);

const Footer = () => {
  const [roles, setRoles] = useState([
    'Accountant','Architect','BPO / Telecaller','Tech Support','Content Writer','Fashion Designer','Data Entry','SEO / Social Media','HR / Admin','Engineer (Core, Non-IT)','Sales Executive','Management Trainee','Design / Animation','Teacher / Trainer','Delivery Executive','Chef / Cook','Counsellor','More Roles'
  ]);
  const [cities, setCities] = useState([
    'Ahmedabad','Bangalore','Chennai','Delhi','Hyderabad','Kolkata','Mumbai','Pune','Noida','Gurgaon','Chandigarh','Panaji','Patna','Bhopal','Ahmednagar','Bhubaneshwar','Pondicherry','Mohali','Jaipur','More Cities'
  ]);
  
  // 🌟 Dynamic Partners & Settings State
  const [partners, setPartners] = useState([]);
  const [settings, setSettings] = useState({
    company_name: 'Kadtech HR Solutions',
    email: 'connect@kadtechhr.com',
    phone: '+91 72596 79676',
    address: 'No. 11, 1st Floor, RT Nagar Main Road, Bangalore – 560032, Karnataka, India',
    copyright_text: `© ${new Date().getFullYear()} Kadtech HR Solutions. All rights reserved.`,
    logo_url: logo
  });

  useEffect(() => {
    const fetchDynamicData = async () => {
      try {
        const res = await api.get("/nav-filters");

        if (res.data.categories) setRoles(res.data.categories);
        if (res.data.cities) setCities(res.data.cities);
      } catch (err) {
        console.error("Error fetching footer filter data", err);
      }
    };

    // 🌟 Fetch Dynamic Active Partners Logos
    const fetchPartners = async () => {
      try {
        const res = await api.get("/public-partners"); 
        if (res.data.status === 'success') {
          setPartners(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching partners", err);
      }
    };

   const fetchSettings = async () => {
      try {
        const res = await api.get("/public-settings");
        if (res.data.status === 'success' && res.data.data) {
          const data = res.data.data;
          setSettings({
            company_name: data.company_name || 'Kadtech HR Solutions',
            email: data.contact_email || 'connect@kadtechhr.com',
            phone: data.contact_number || '+91 72596 79676',
            address: data.office_address || 'No. 11, 1st Floor, RT Nagar Main Road, Bangalore – 560032, Karnataka, India',
            copyright_text: data.copyright_text || `© ${new Date().getFullYear()} Kadtech HR Solutions. All rights reserved.`,
            logo_url: data.logo_url || logo
          });
        }
      } catch (err) {
        console.error("Error fetching website settings", err);
      }
    };

    fetchDynamicData();
    fetchPartners();
    fetchSettings();
  }, []);

  return (
    <>
      <SEOContent />

      <section className="bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-[1200px] mx-auto px-4 py-8 space-y-6">
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-sm">Jobs By Roles</h4>
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
              {roles.map((r, i) => (
                <React.Fragment key={i}>
                  <a href={`/job-listing?category=${encodeURIComponent(r)}`} className="hover:text-[#FED547] transition-colors">{r}</a>
                  {i < roles.length - 1 && <span className="text-gray-300 dark:text-gray-600">|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-sm">Jobs By Cities</h4>
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
              {cities.map((c, i) => (
                <React.Fragment key={i}>
                  <a href={`/job-listing?city=${encodeURIComponent(c)}`} className="hover:text-[#FED547] transition-colors">{c}</a>
                  {i < cities.length - 1 && <span className="text-gray-300 dark:text-gray-600">|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer
        className="relative overflow-hidden transition-colors duration-300"
        style={{ background: 'linear-gradient(135deg, #060606ff 0%, #393a3dff 55%, #8f929aff 100%)' }}
      >
        <div className="max-w-[1200px] mx-auto px-4 py-12 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Dynamic Logo & Company Name */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-24 h-24 flex items-center justify-center bg-white rounded-lg p-1 dark:bg-gray-800">
              <img src={settings.logo_url} alt={settings.company_name} className="w-full h-full object-contain" />
            </div>
            
            {/* UPDATED HEADER STYLE TEXT */}
            <div className="leading-tight">
              <div className="font-extrabold text-[24px] text-white tracking-tight">
                Kadtech<span className="text-[#FED547]">.</span>
              </div>
              <div className="text-[14px] text-gray-400 font-medium tracking-wider uppercase">
                HR Solutions
              </div>
            </div>

          </div>

          <div>
            <h5 className="font-bold text-white mb-3 text-sm">For Job Seekers</h5>
            <ul className="space-y-2 text-sm">
              {["Browse Jobs", "Create Profile", "Career Advice", "Post Your Resume", "Salary Calculator", "Job Alerts"].map((l) => (
                <li key={l}>
                  <a href="#" className="text-white hover:text-[#FED547] transition-colors duration-300">{l}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="font-bold text-white mb-3 text-sm">For Employers</h5>
            <ul className="space-y-2 text-sm">
              {["Post a Job", "Browse Candidates", "Employer Login", "Pricing", "Bulk Hiring", "Contact Sales"].map((l) => (
                <li key={l}>
                  <a href="#" className="text-white hover:text-[#FED547] transition-colors duration-300">{l}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Dynamic Contact Details (Address, Phone, Email) */}
          <div>
            <h5 className="font-bold text-white mb-3 text-sm">Get in Touch</h5>
            <ul className="space-y-4 text-sm text-gray-400">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1 shrink-0 text-[#FED547]" />
                <span className="whitespace-pre-line">{settings.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 shrink-0 text-[#FED547]" />
                <a href={`tel:${settings.phone}`} className="hover:text-[#FED547] transition-colors">{settings.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 shrink-0 text-[#FED547]" />
                <a href={`mailto:${settings.email}`} className="hover:text-[#FED547] transition-colors break-all">{settings.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border-t border-gray-300 dark:border-gray-700">
          <div className="max-w-[1200px] mx-auto px-4 py-4 flex flex-col lg:flex-row items-center justify-between gap-6 text-xs dark:text-gray-300 text-black">
            
            {/* Dynamic Copyright Text */}
            <div>{settings.copyright_text}</div>
            
            {/* Dynamic Partner Sites Section */}
            <div className="flex items-center gap-6">
              <h5 className="text-black dark:text-white font-semibold text-sm whitespace-nowrap">Partner Sites</h5>
              <div className="flex flex-nowrap items-center gap-6 overflow-x-auto max-w-lg">
                {partners.length > 0 ? (
                  partners.map((partner) => (
                    <a 
                      key={partner.id} 
                      href={partner.website_url || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-28 h-10 flex items-center justify-center flex-shrink-0"
                    >
                      <img 
                        src={partner.logo_url} 
                        alt={partner.name} 
                        className="max-h-8 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300" 
                      />
                    </a>
                  ))
                ) : (
                  <span className="text-gray-400 text-xs italic">No partner sites available</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px' }}>
              <Link to="/privacy-policy" style={{ color: '#000000', textDecoration: 'none' }}>Privacy Policy</Link>
              <Link to="/terms-and-conditions" style={{ color: '#000000', textDecoration: 'none' }}>Terms of Service</Link>
              <Link to="/sitemap" style={{ color: '#000000', textDecoration: 'none' }}>Sitemap</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;